import React, { useState, useEffect } from "react";
import { Col, Row, Card, Form, Button, Alert, ProgressBar } from '@themesberg/react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCrown, faCheckCircle, faTimesCircle, faPlus, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { useHistory } from 'react-router-dom';
import { uploadToR2 } from '../../utils/r2Upload';
import api from '../../utils/api';
import { Routes } from "../../routes";

const STORY_TYPES = { text: 'Metin', image: 'Resim', video: 'Video' };

export default () => {
  const history = useHistory();
  const [fakeAccounts, setFakeAccounts] = useState([]);
  const [mediaFile, setMediaFile] = useState(null);
  const [mediaPreview, setMediaPreview] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [formData, setFormData] = useState({
    fakeAccountSelect: "",
    storyType: "text",
    storyText: "",
    backgroundColor: "#262b40",
    duration: 24,
    durationUnit: "hour",
    vipStory: false,
    activeStory: true,
    autoDelete: true
  });

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const res = await api.get('/users?isFake=true&limit=500');
        const users = res.data?.users || [];
        setFakeAccounts(users.map(u => ({
          id: u.uid || u.id || u._id,
          name: u.name || u.displayName || u.firstName || 'Bilinmeyen',
          avatar: u.profileImageUrl || null
        })));
      } catch (err) {
        console.error("Error loading fake accounts:", err);
        setError("Fake hesaplar yüklenemedi: " + (err.response?.data?.error || err.message));
      }
    };
    fetchAccounts();
  }, []);

  const updateField = (name, value) => setFormData(prev => ({ ...prev, [name]: value }));

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setMediaFile(file);
    setMediaPreview(URL.createObjectURL(file));
  };

  const clearFile = () => {
    setMediaFile(null);
    setMediaPreview(null);
  };

  const computeDeleteAt = () => {
    if (!formData.autoDelete) return null;
    const value = parseInt(formData.duration, 10);
    if (!value || value < 1) return null;

    const target = new Date();
    if (formData.durationUnit === 'day') target.setDate(target.getDate() + value);
    else target.setHours(target.getHours() + value);
    return target.toISOString();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    const account = fakeAccounts.find(a => a.id === formData.fakeAccountSelect);
    if (!account) {
      setError("Lütfen bir fake hesap seçin.");
      return;
    }
    if (formData.storyType === 'text' && !formData.storyText.trim()) {
      setError("Metin hikayesi için hikaye metni zorunludur.");
      return;
    }
    if (formData.storyType !== 'text' && !mediaFile) {
      setError(`${STORY_TYPES[formData.storyType]} hikayesi için dosya seçmelisiniz.`);
      return;
    }

    setSaving(true);
    setUploadProgress(0);

    try {
      let mediaUrl = null;
      if (mediaFile) {
        const extension = mediaFile.name.split('.').pop();
        const path = `stories/${account.id}/${Date.now()}.${extension}`;
        mediaUrl = await uploadToR2(mediaFile, path, setUploadProgress);
      }

      const isVideo = formData.storyType === 'video';

      await api.post('/content/admin-create', {
        type: 'story',
        userId: account.id,
        authorId: account.id,
        authorName: account.name,
        authorAvatar: account.avatar,
        text: formData.storyText,
        mediaUrl,
        imageUrl: isVideo ? null : mediaUrl,
        videoUrl: isVideo ? mediaUrl : null,
        mediaType: formData.storyType,
        isVideo,
        backgroundColor: formData.backgroundColor,
        isVip: formData.vipStory,
        isActive: formData.activeStory,
        isApproved: true,
        isVisibleToOthers: true,
        deleteAt: computeDeleteAt()
      });

      setSuccess("Hikaye başarıyla oluşturuldu.");
      setTimeout(() => history.push(Routes.StoriesList.path), 1200);
    } catch (err) {
      console.error("Error creating story:", err);
      setError("Hikaye oluşturulamadı: " + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <div className="py-4">
        <h4>Ekle</h4>
        <p className="text-muted mb-0">Hikayeler / Ekle</p>
      </div>

      {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      <Card className="border-0 shadow-sm mb-4">
        <Card.Body>
          <h5 className="mb-3">Fake Kullanıcı Seçimi</h5>
          <p className="text-muted small">Hikaye eklemek için kullanıcı seçin</p>

          <Form onSubmit={handleSubmit}>
            <Row className="mb-4">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>
                    Hesap Seçin <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    as="select"
                    name="fakeAccountSelect"
                    value={formData.fakeAccountSelect}
                    onChange={(e) => updateField('fakeAccountSelect', e.target.value)}
                    required
                  >
                    <option value="">Seçiniz...</option>
                    {fakeAccounts.map(account => (
                      <option key={account.id} value={account.id}>{account.name}</option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
            </Row>

            <h5 className="mb-3 mt-4">Hikaye Oluştur</h5>
            <p className="text-muted small">Yeni bir hikaye ekleyin</p>

            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>
                    Hikaye Türü <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    as="select"
                    name="storyType"
                    value={formData.storyType}
                    onChange={(e) => {
                      updateField('storyType', e.target.value);
                      clearFile();
                    }}
                    required
                  >
                    {Object.entries(STORY_TYPES).map(([value, label]) => (
                      <option key={value} value={value}>{label}</option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
            </Row>

            {formData.storyType !== 'text' && (
              <Row className="mb-3">
                <Col md={12}>
                  <Form.Group>
                    <Form.Label>
                      {STORY_TYPES[formData.storyType]} Dosyası <span className="text-danger">*</span>
                    </Form.Label>
                    <Form.Control
                      type="file"
                      accept={formData.storyType === 'video' ? 'video/*' : 'image/*'}
                      onChange={handleFileChange}
                    />
                    {mediaPreview && (
                      <div className="mt-3 d-flex align-items-center gap-3">
                        {formData.storyType === 'video' ? (
                          <video src={mediaPreview} style={{ width: 160, borderRadius: 8 }} controls />
                        ) : (
                          <img src={mediaPreview} alt="önizleme" style={{ width: 120, borderRadius: 8 }} />
                        )}
                        <Button variant="light" size="sm" onClick={clearFile}>
                          <FontAwesomeIcon icon={faTimesCircle} className="me-1" /> Kaldır
                        </Button>
                      </div>
                    )}
                  </Form.Group>
                </Col>
              </Row>
            )}

            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>
                    Hikaye Metni {formData.storyType === 'text' && <span className="text-danger">*</span>}
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="storyText"
                    placeholder="Hikaye metninizi yazın..."
                    value={formData.storyText}
                    onChange={(e) => updateField('storyText', e.target.value)}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={12}>
                <Form.Group>
                  <Form.Label>Arkaplan Rengi</Form.Label>
                  <div className="d-flex align-items-center gap-2">
                    <Form.Control
                      type="color"
                      name="backgroundColor"
                      value={formData.backgroundColor}
                      onChange={(e) => updateField('backgroundColor', e.target.value)}
                      style={{ width: '60px', height: '40px' }}
                    />
                    <Button variant="light" size="sm" onClick={() => updateField('backgroundColor', '')}>
                      <FontAwesomeIcon icon={faTimesCircle} className="text-muted" />
                    </Button>
                  </div>
                </Form.Group>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={6}>
                <Form.Group>
                  <Form.Label>
                    Süre <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    type="number"
                    name="duration"
                    value={formData.duration}
                    onChange={(e) => updateField('duration', e.target.value)}
                    min="1"
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group>
                  <Form.Label>
                    Süre Birimi <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Control
                    as="select"
                    name="durationUnit"
                    value={formData.durationUnit}
                    onChange={(e) => updateField('durationUnit', e.target.value)}
                    required
                  >
                    <option value="hour">Saat</option>
                    <option value="day">Gün</option>
                  </Form.Control>
                </Form.Group>
              </Col>
            </Row>

            <h5 className="mb-3 mt-4">Hikaye Ayarları</h5>

            <Row className="mb-3">
              <Col md={12}>
                <Card className="border-0 bg-light">
                  <Card.Body className="py-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <div className="d-flex align-items-center gap-2">
                        <FontAwesomeIcon icon={faCrown} className="text-warning" />
                        <div>
                          <strong>VIP Hikaye</strong>
                          <p className="mb-0 small text-muted">Sadece VIP üyelere göster</p>
                        </div>
                      </div>
                      <Form.Check
                        type="switch"
                        id="vip-story"
                        checked={formData.vipStory}
                        onChange={(e) => updateField('vipStory', e.target.checked)}
                      />
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row className="mb-3">
              <Col md={12}>
                <Card className="border-0 bg-light">
                  <Card.Body className="py-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <div className="d-flex align-items-center gap-2">
                        <FontAwesomeIcon icon={faCheckCircle} className="text-success" />
                        <div>
                          <strong>Aktif</strong>
                          <p className="mb-0 small text-muted">Hikayesi hemen yayınla</p>
                        </div>
                      </div>
                      <Form.Check
                        type="switch"
                        id="active-story"
                        checked={formData.activeStory}
                        onChange={(e) => updateField('activeStory', e.target.checked)}
                      />
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            <Row className="mb-4">
              <Col md={12}>
                <Card className="border-0 bg-light">
                  <Card.Body className="py-3">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <div className="d-flex align-items-center gap-2">
                        <FontAwesomeIcon icon={faTimesCircle} className="text-danger" />
                        <div>
                          <strong>Otomatik Sil</strong>
                          <p className="mb-0 small text-muted">Süre sonunda otomatik sil</p>
                        </div>
                      </div>
                      <Form.Check
                        type="switch"
                        id="auto-delete"
                        checked={formData.autoDelete}
                        onChange={(e) => updateField('autoDelete', e.target.checked)}
                      />
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {saving && mediaFile && (
              <ProgressBar now={uploadProgress} label={`${uploadProgress}%`} className="mb-3" />
            )}

            <div className="d-flex justify-content-start">
              <Button variant="primary" type="submit" size="lg" disabled={saving}>
                <FontAwesomeIcon icon={saving ? faSpinner : faPlus} className={saving ? "me-2 fa-spin" : "me-2"} />
                {saving ? 'Kaydediliyor...' : 'Hikaye Oluştur'}
              </Button>
            </div>
          </Form>
        </Card.Body>
      </Card>
    </>
  );
};
